import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Group, Modal, MultiSelect, Select, Stack, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateProjectRequest } from '@ledokol/contracts'
import { createProject, updateProject } from '../../shared/api/projects'
import { createAdvertiser, updateAdvertiser } from '../../shared/api/advertisers'
import { fetchDepartments } from '../../shared/api/departments'
import { fetchFunnelSegments } from '../../shared/api/funnel-segments'
import { fetchUsers } from '../../shared/api/users'
import { useAuthStore } from '../auth/store'

/**
 * Создание рекламодателей/проектов без Telegram (старая база) и переименование.
 * docs/superpowers/specs/2026-09-24-projects-without-tg-design.md §2.7.
 * Поля проекта — те же, что правятся у существующих: название, тип, отделы, лид,
 * участники, сегмент воронки, статус (active/pause; finished — штатной сменой статуса).
 */

type ApiError = {
  response?: { status?: number; data?: { error?: { code?: string; entity?: DuplicateEntity; message?: string } } }
}
type DuplicateEntity = 'project' | 'advertiser'
const apiError = (e: unknown) => (e as ApiError).response?.data?.error
const errorMessage = (e: unknown) => apiError(e)?.message ?? 'Не удалось сохранить'

type AllowDuplicate = Record<DuplicateEntity, boolean>
const NO_DUPLICATES_ALLOWED: AllowDuplicate = { project: false, advertiser: false }

/**
 * Мягкая защита от дублей (409 duplicate_name). Подтверждение — ОТДЕЛЬНО по сущности
 * (error.entity): «всё равно создать» для рекламодателя не пропускает молча дубль его
 * первого проекта — сервер вернёт второй 409, и пользователь подтвердит и его.
 */
function useDuplicateConfirm(submit: (allow: AllowDuplicate) => Promise<void>, onDone: () => void) {
  const [allowed, setAllowed] = useState<AllowDuplicate>(NO_DUPLICATES_ALLOWED)
  const [duplicate, setDuplicate] = useState<{ entity: DuplicateEntity; message: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function run(allow: AllowDuplicate) {
    setSubmitting(true)
    try {
      await submit(allow)
      onDone()
    } catch (e) {
      const err = apiError(e)
      if (err?.code === 'duplicate_name' && err.entity) setDuplicate({ entity: err.entity, message: errorMessage(e) })
      else notifications.show({ color: 'red', title: 'Не удалось создать', message: errorMessage(e) })
    } finally {
      setSubmitting(false)
    }
  }

  return {
    duplicate,
    submitting,
    submit: () => run(allowed),
    confirmDuplicate: () => {
      if (!duplicate) return
      const next = { ...allowed, [duplicate.entity]: true }
      setAllowed(next)
      setDuplicate(null)
      void run(next)
    },
    /** Любая правка формы → проверка дублей заново. */
    reset: () => {
      setAllowed(NO_DUPLICATES_ALLOWED)
      setDuplicate(null)
    },
  }
}

interface ProjectFieldsState {
  name: string
  type: 'current' | 'new'
  departments: string[]
  leadUserId: string | null
  memberIds: string[]
  segmentId: string | null
  status: 'active' | 'pause'
}

function useProjectFieldsInit(): ProjectFieldsState {
  const user = useAuthStore((s) => s.user)
  return {
    name: '',
    type: 'current',
    // head создаёт только в свои отделы (сервер проверит) — предвыбираем единственный
    departments: user?.role === 'head' && user.departments.length === 1 ? [user.departments[0]!.id] : [],
    leadUserId: user?.id ?? null,
    memberIds: [],
    segmentId: null,
    status: 'active',
  }
}

function toCreateRequest(f: ProjectFieldsState): CreateProjectRequest {
  return {
    name: f.name.trim(),
    type: f.type,
    departments: f.departments,
    lead_user_id: f.leadUserId!,
    member_ids: f.memberIds,
    segment_id: f.segmentId,
    status: f.status,
  }
}

const projectFieldsValid = (f: ProjectFieldsState) => f.name.trim().length > 0 && f.departments.length > 0 && !!f.leadUserId

function ProjectFields({ value, onChange }: { value: ProjectFieldsState; onChange: (v: ProjectFieldsState) => void }) {
  const user = useAuthStore((s) => s.user)
  const depts = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })
  const users = useQuery({ queryKey: ['users', 'active'], queryFn: () => fetchUsers({ active: true }) })
  const segments = useQuery({ queryKey: ['funnel-segments'], queryFn: fetchFunnelSegments })
  const set = (patch: Partial<ProjectFieldsState>) => onChange({ ...value, ...patch })

  const deptOptions = useMemo(() => {
    const all = (depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))
    if (user?.role !== 'head') return all
    const own = new Set(user.departments.map((d) => d.id))
    return all.filter((d) => own.has(d.value))
  }, [depts.data, user])
  const userOptions = useMemo(
    () => (users.data ?? []).map((u) => ({ value: u.id, label: u.tg_username ? `${u.full_name} — @${u.tg_username}` : u.full_name })),
    [users.data],
  )

  return (
    <>
      <TextInput label="Название проекта" value={value.name} onChange={(e) => set({ name: e.currentTarget.value })} required maxLength={200} />
      <Group grow>
        <Select
          label="Тип"
          data={[{ value: 'current', label: 'Текущий клиент' }, { value: 'new', label: 'Новый клиент' }]}
          value={value.type}
          onChange={(v) => v && set({ type: v as ProjectFieldsState['type'] })}
          allowDeselect={false}
        />
        <Select
          label="Статус"
          data={[{ value: 'active', label: 'Активен' }, { value: 'pause', label: 'Пауза' }]}
          value={value.status}
          onChange={(v) => v && set({ status: v as ProjectFieldsState['status'] })}
          allowDeselect={false}
        />
      </Group>
      <MultiSelect label="Отделы" data={deptOptions} value={value.departments} onChange={(v) => set({ departments: v })} required searchable />
      <Select label="Лид проекта" data={userOptions} value={value.leadUserId} onChange={(v) => set({ leadUserId: v })} required searchable />
      <MultiSelect label="Участники" data={userOptions} value={value.memberIds} onChange={(v) => set({ memberIds: v })} searchable clearable />
      <Select
        label="Сегмент воронки"
        data={(segments.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
        value={value.segmentId}
        onChange={(v) => set({ segmentId: v })}
        clearable
      />
    </>
  )
}

/** Предупреждение о дубле + кнопки. Дубль не блокирует — «Всё равно создать». */
function DuplicateAwareFooter({
  dup,
  valid,
  onClose,
}: {
  dup: ReturnType<typeof useDuplicateConfirm>
  valid: boolean
  onClose: () => void
}) {
  const text = dup.duplicate ? `${dup.duplicate.message[0]!.toUpperCase()}${dup.duplicate.message.slice(1)}.` : ''
  return (
    <>
      {dup.duplicate && (
        <Alert color="yellow" variant="light">
          {text} Проверьте список — возможно, его уже завели. Если это другой клиент — создайте всё равно.
        </Alert>
      )}
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose} disabled={dup.submitting}>Отмена</Button>
        {dup.duplicate ? (
          <Button color="yellow" onClick={dup.confirmDuplicate} loading={dup.submitting}>Всё равно создать</Button>
        ) : (
          <Button onClick={dup.submit} loading={dup.submitting} disabled={!valid}>Создать</Button>
        )}
      </Group>
    </>
  )
}

function useInvalidateProjects() {
  const qc = useQueryClient()
  return (projectId?: string) => {
    void qc.invalidateQueries({ queryKey: ['projects'] })
    void qc.invalidateQueries({ queryKey: ['advertisers'] })
    if (projectId) void qc.invalidateQueries({ queryKey: ['project', projectId] })
  }
}

export function CreateAdvertiserModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const init = useProjectFieldsInit()
  const user = useAuthStore((s) => s.user)
  const canCreateProject = user?.permissions.includes('projects.create') ?? false
  const [name, setName] = useState('')
  const [withProject, setWithProject] = useState(false)
  const [project, setProject] = useState(init)
  const invalidate = useInvalidateProjects()
  const dup = useDuplicateConfirm(
    async (allow) => {
      await createAdvertiser({
        name: name.trim(),
        ...(allow.advertiser && { allow_duplicate: true }),
        ...(withProject && { first_project: { ...toCreateRequest(project), ...(allow.project && { allow_duplicate: true }) } }),
      })
      notifications.show({ color: 'green', message: 'Рекламодатель создан' })
      invalidate()
    },
    onClose,
  )

  // init пересоздаётся на каждый рендер — сбрасываем форму только при открытии
  useEffect(() => {
    if (!opened) return
    setName('')
    setWithProject(false)
    setProject(init)
    dup.reset()
  }, [opened])

  const valid = name.trim().length > 0 && (!withProject || projectFieldsValid(project))
  return (
    <Modal opened={opened} onClose={onClose} title="Новый рекламодатель" centered size="lg">
      <Stack>
        <TextInput
          label="Название рекламодателя"
          value={name}
          onChange={(e) => { setName(e.currentTarget.value); dup.reset() }}
          required
          maxLength={200}
          data-autofocus
        />
        {canCreateProject && (
          <Checkbox
            label="Сразу создать первый проект"
            checked={withProject}
            onChange={(e) => { setWithProject(e.currentTarget.checked); dup.reset() }}
          />
        )}
        {withProject && <ProjectFields value={project} onChange={(v) => { setProject(v); dup.reset() }} />}
        <DuplicateAwareFooter dup={dup} valid={valid} onClose={onClose} />
      </Stack>
    </Modal>
  )
}

export function CreateProjectModal({
  advertiser,
  onClose,
}: {
  advertiser: { id: string; name: string } | null
  onClose: () => void
}) {
  const init = useProjectFieldsInit()
  const [project, setProject] = useState(init)
  const invalidate = useInvalidateProjects()
  const dup = useDuplicateConfirm(
    async (allow) => {
      await createProject({ ...toCreateRequest(project), advertiser_id: advertiser?.id ?? null, ...(allow.project && { allow_duplicate: true }) })
      notifications.show({ color: 'green', message: 'Проект создан' })
      invalidate()
    },
    onClose,
  )

  useEffect(() => {
    if (!advertiser) return
    setProject({ ...init, name: `${advertiser.name} · ` })
    dup.reset()
  }, [advertiser?.id])

  return (
    <Modal opened={!!advertiser} onClose={onClose} title={advertiser ? `Новый проект · ${advertiser.name}` : ''} centered size="lg">
      <Stack>
        <ProjectFields value={project} onChange={(v) => { setProject(v); dup.reset() }} />
        <DuplicateAwareFooter dup={dup} valid={projectFieldsValid(project)} onClose={onClose} />
      </Stack>
    </Modal>
  )
}

/**
 * Переименование проекта или рекламодателя. Проект: PATCH /projects/:id (name_manual → бот
 * больше не перезаписывает имя при ренейме топика). Рекламодатель: PATCH /advertisers/:id.
 */
export function RenameModal({
  target,
  onClose,
}: {
  target: { kind: 'project' | 'advertiser'; id: string; name: string } | null
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const invalidate = useInvalidateProjects()
  // Сброс поля — только при смене цели (открытие/другая сущность), а НЕ при каждом новом объекте
  // target: родитель пересоздаёт его на ре-рендере (рефетч карточки) — иначе ввод затирается.
  const targetKey = target ? `${target.kind}:${target.id}` : null
  useEffect(() => { if (target) setName(target.name) }, [targetKey])

  async function save() {
    if (!target) return
    setSubmitting(true)
    try {
      if (target.kind === 'project') await updateProject(target.id, { name: name.trim() })
      else await updateAdvertiser(target.id, { name: name.trim() })
      notifications.show({ color: 'green', message: 'Переименовано' })
      invalidate(target.kind === 'project' ? target.id : undefined)
      onClose()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Не удалось переименовать', message: errorMessage(e) })
    } finally {
      setSubmitting(false)
    }
  }

  const trimmed = name.trim()
  return (
    <Modal
      opened={!!target}
      onClose={onClose}
      title={target?.kind === 'advertiser' ? 'Переименовать рекламодателя' : 'Переименовать проект'}
      centered
    >
      <Stack>
        <TextInput
          label="Название"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          maxLength={200}
          error={trimmed.length === 0 ? 'Название не может быть пустым' : undefined}
          data-autofocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>Отмена</Button>
          <Button onClick={save} loading={submitting} disabled={trimmed.length === 0 || trimmed === target?.name}>Сохранить</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
